import { Alpine } from 'alpinejs';

declare global {
  interface Window {
    Alpine: Alpine;
  }
}

interface PostListingData {
  // State properties
  currentPage: number;
  maxPages: number;
  loadingNext: boolean;
  isLoading: boolean;

  // Configuration properties
  containerTarget: string;
  wrapperTarget: string;
  pagerTarget: string;
  postSelector: string;
  orderByFieldSelector: string;
  showPagination: boolean;

  // Observer
  observer: IntersectionObserver | null;

  // Public methods
  init(): void;
  loadMore(url: string): void;
  handlePostsOrderBy(orderByValue: string): void;
  handlePaginationNav(url: string): void;
  getPageUrl(page: number): string;
  hasMorePages(): boolean;
  updateCurrentPage(): void;

  // Private utility methods
  initializeDataAttributes(): void;
  extractPageFromUrl(url: string): number;
  updateCurrentPageFromCurrentUrl(): void;
  updateCurrentPageFromUrl(url: string): void;
  buildOrderByUrl(orderByValue: string, includeAjax?: boolean): string;
  addAjaxParameter(url: string): string;
  getAjaxTarget(): string;
  validateTarget(target: string): boolean;
  updateBrowserUrl(orderByValue: string): void;
  updateBrowserUrlFromCleanUrl(url: string): void;
  cleanUrlFromAjaxParam(): void;
  fallbackToPageNavigation(url: string): void;
  makeAjaxRequest(
    url: string,
    options: {
      target: string;
      swap: string;
      onSuccess?: () => void;
      onError?: (error: any) => void;
      onFinally?: () => void;
    },
  ): void;
  setupIntersectionObserver(): void;
  handleIntersectionEntry(entry: IntersectionObserverEntry): void;
  updateUrlForPageChange(pageNumber: number): void;
}

export const postListing = (): PostListingData => ({
  // State
  currentPage: 1,
  maxPages: 1,
  loadingNext: false,
  isLoading: false,

  // Configuration
  containerTarget: '',
  wrapperTarget: '',
  pagerTarget: '',
  postSelector: '',
  orderByFieldSelector: '',
  showPagination: false,

  // Observer
  observer: null as IntersectionObserver | null,

  // Initialize component
  init() {
    this.initializeDataAttributes();
    this.updateCurrentPage();
  },

  // Initialize data attributes from DOM element
  initializeDataAttributes() {
    const el = this.$el as HTMLElement;
    if (!el) return;

    this.containerTarget = el.dataset.containerTarget || '';
    this.wrapperTarget = el.dataset.wrapperTarget || '';
    this.pagerTarget = el.dataset.pagerTarget || '';
    this.postSelector = el.dataset.postSelector || '';
    this.orderByFieldSelector = el.dataset.orderByField || '';
    this.showPagination = el.dataset.showPagination === 'true';
  },

  // Load more posts (append behavior)
  loadMore(url: string) {
    if (this.loadingNext || !url || !this.containerTarget) return;

    this.loadingNext = true;

    this.makeAjaxRequest(url, {
      target: this.containerTarget,
      swap: 'append',
      onSuccess: () => {
        this.updateCurrentPageFromUrl(url);
        this.updateCurrentPage();
      },
      onError: (error) => {
        console.error('Load more error:', error);
      },
      onFinally: () => {
        this.loadingNext = false;
      },
    });
  },

  // Handle order by changes
  handlePostsOrderBy(orderByValue: string) {
    if (this.isLoading || !orderByValue || !this.orderByFieldSelector) return;

    this.isLoading = true;

    const url = this.buildOrderByUrl(orderByValue);
    const target = this.getAjaxTarget();

    if (!this.validateTarget(target)) {
      this.isLoading = false;
      return;
    }

    this.makeAjaxRequest(url, {
      target,
      swap: 'innerHTML',
      onSuccess: () => {
        this.updateBrowserUrl(orderByValue);
        this.updateCurrentPage();
      },
      onError: (error) => {
        console.error('Order by error:', error);
        this.fallbackToPageNavigation(
          this.buildOrderByUrl(orderByValue, false),
        );
      },
      onFinally: () => {
        this.isLoading = false;
      },
    });
  },

  // Handle pagination navigation
  handlePaginationNav(url: string) {
    if (this.isLoading) return;

    this.isLoading = true;

    const targetPage = this.extractPageFromUrl(url);
    const ajaxUrl = this.addAjaxParameter(url);
    const target = this.getAjaxTarget();

    if (!this.validateTarget(target)) {
      this.isLoading = false;
      return;
    }

    this.makeAjaxRequest(ajaxUrl, {
      target,
      swap: 'innerHTML',
      onSuccess: () => {
        this.currentPage = targetPage;
        this.updateBrowserUrlFromCleanUrl(url);
        this.updateCurrentPage();
      },
      onError: (error) => {
        console.error('Pagination navigation error:', error);
        this.fallbackToPageNavigation(url);
      },
      onFinally: () => {
        this.isLoading = false;
      },
    });
  },

  // Generate page URL
  getPageUrl(page: number): string {
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('paged', page.toString());
    } else {
      url.searchParams.delete('paged');
    }
    return url.toString();
  },

  // Check if there are more pages to load
  hasMorePages(): boolean {
    return this.currentPage < this.maxPages;
  },

  // Update current page and setup observer
  updateCurrentPage() {
    this.updateCurrentPageFromCurrentUrl();
    this.cleanUrlFromAjaxParam();
    this.setupIntersectionObserver();
  },

  // === PRIVATE UTILITY METHODS ===

  // Extract page number from URL
  extractPageFromUrl(url: string): number {
    const urlObj = new URL(url);
    const pageParam = urlObj.searchParams.get('paged');
    return pageParam ? parseInt(pageParam, 10) : 1;
  },

  // Update current page from URL
  updateCurrentPageFromCurrentUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageFromUrl = urlParams.get('paged');
    const newPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;

    if (this.currentPage !== newPage) {
      this.currentPage = newPage;
    }
  },

  // Update current page from specific URL
  updateCurrentPageFromUrl(url: string) {
    const pageParam = this.extractPageFromUrl(url);
    if (pageParam) {
      this.currentPage = pageParam;
    }
  },

  // Build order by URL
  buildOrderByUrl(orderByValue: string, includeAjax: boolean = true): string {
    const url = new URL(window.location.href);
    url.searchParams.set(this.orderByFieldSelector, orderByValue);
    url.searchParams.delete('paged');

    if (includeAjax) {
      url.searchParams.set('ajax', '1');
    }

    return url.toString();
  },

  // Add ajax parameter to URL
  addAjaxParameter(url: string): string {
    const urlObj = new URL(url);
    urlObj.searchParams.set('ajax', '1');
    return urlObj.toString();
  },

  // Get AJAX target element selector
  getAjaxTarget(): string {
    return this.wrapperTarget || this.containerTarget;
  },

  // Validate target element exists
  validateTarget(target: string): boolean {
    const targetElement =
      document.getElementById(target) || document.querySelector(`.${target}`);
    return !!targetElement;
  },

  // Update browser URL after order by change
  updateBrowserUrl(orderByValue: string) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.set(this.orderByFieldSelector, orderByValue);
    cleanUrl.searchParams.delete('paged');
    window.history.pushState({}, '', cleanUrl.toString());
  },

  // Update browser URL from clean URL
  updateBrowserUrlFromCleanUrl(url: string) {
    const cleanUrl = new URL(url);
    cleanUrl.searchParams.delete('ajax');
    window.history.pushState({}, '', cleanUrl.toString());
  },

  // Clean URL from ajax parameter
  cleanUrlFromAjaxParam() {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('ajax')) {
      currentUrl.searchParams.delete('ajax');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  },

  // Fallback to page navigation
  fallbackToPageNavigation(url: string) {
    window.location.href = url;
  },

  // Generic AJAX request wrapper
  makeAjaxRequest(
    url: string,
    options: {
      target: string;
      swap: string;
      onSuccess?: () => void;
      onError?: (error: any) => void;
      onFinally?: () => void;
    },
  ) {
    this.$ajax?.(url, {
      target: options.target,
      swap: options.swap,
    })
      .then(() => {
        options.onSuccess?.();
      })
      .catch((error: any) => {
        options.onError?.(error);
      })
      .finally(() => {
        options.onFinally?.();
      });
  },

  // Setup intersection observer for pagination
  setupIntersectionObserver() {
    // Reset observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.$nextTick?.(() => {
      if (!this.postSelector) return;

      const blocks = document.querySelectorAll(`.${this.postSelector}`);
      if (!blocks.length) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.handleIntersectionEntry(entry);
            }
          });
        },
        { threshold: 0.5, rootMargin: '0px 0px -50px 0px' },
      );

      blocks.forEach((block) => this.observer?.observe(block));
    });
  },

  // Handle intersection observer entry
  handleIntersectionEntry(entry: IntersectionObserverEntry) {
    const page = entry.target.getAttribute('data-page');
    if (!page) return;

    const pageNumber = parseInt(page, 10);
    if (isNaN(pageNumber) || this.currentPage === pageNumber) return;

    this.currentPage = pageNumber;

    if (this.showPagination) {
      this.updateUrlForPageChange(pageNumber);
    }
  },

  // Update URL for page change via intersection observer
  updateUrlForPageChange(pageNumber: number) {
    const currentUrl = new URL(window.location.href);
    const currentPageInUrl = currentUrl.searchParams.get('paged');
    const currentPageNumber = currentPageInUrl
      ? parseInt(currentPageInUrl, 10)
      : 1;

    // Only update URL if the page in URL doesn't match target page
    if (currentPageNumber !== pageNumber) {
      if (pageNumber > 1) {
        const newUrl = this.getPageUrl(pageNumber);
        history.replaceState(null, '', newUrl);
      } else {
        // Remove paged parameter if we're back to page 1
        if (currentUrl.searchParams.has('paged')) {
          currentUrl.searchParams.delete('paged');
          history.replaceState(null, '', currentUrl.toString());
        }
      }
    }
  },
});
